/**
 * The anchor strip (R8, Doom baseline 10-20 evidence): matchup · PT date and time · period and clock · score · settings
 * version · mode, on the Penelope ticket and beside Game Detail's book, so every screenshot carries its own time, game
 * clock and score. Display only.
 */
import type { PenelopeGame } from '@/hooks/usePenelope';
import { useSharedNow } from '@/lib/infra/sharedClock';
import { ptClock, ptDate } from '@/lib/infra/ptTime';
import { cn } from '@/lib/infra/utils';
import { formatGameStatus } from '@/lib/sport/periodLabel';
import { useGameStateFor } from '@/stores/gameStatesStore';
import type { GameRow } from '@/views/ValueDashboard/types';

/** The game-state facts the strip reads: the relay's (on a running row) or the dashboard's own poll. */
export interface AnchorState {
  statusId?: number | null;
  awayScore: number | null;
  homeScore: number | null;
  gameClock: string | null;
  periodLabel?: string | null;
}

export interface AnchorInput {
  away: string;
  home: string;
  nowMs: number;
  sport?: string | null;
  state: AnchorState | null;
  configVersion?: number | null;
  mode?: 'live' | 'practice' | null;
}

/** The strip's parts, in order. A missing score reads "score ?", never 0–0. */
export function anchorParts(i: AnchorInput): string[] {
  const s = i.state;
  const clock = s ? formatGameStatus({ gameClock: s.gameClock, periodLabel: s.periodLabel ?? null }, i.sport) : null;
  const phase = clock ?? (s?.statusId === 1 ? 'pregame' : s?.statusId === 3 ? 'final' : 'clock ?');
  const score = s && typeof s.awayScore === 'number' && typeof s.homeScore === 'number' ? `${s.awayScore}–${s.homeScore}` : 'score ?';
  const parts = [`${i.away}@${i.home}`, `${ptDate(i.nowMs).slice(5)} ${ptClock(i.nowMs)}`, phase, score];
  if (i.configVersion) parts.push(`v${i.configVersion}`);
  if (i.mode) parts.push(i.mode);
  return parts;
}

export function PenelopeAnchorStrip({ game, row, compact = false, className }: { game: GameRow; row: PenelopeGame | null | undefined; compact?: boolean; className?: string }) {
  const nowMs = useSharedNow(1000);
  const polled = useGameStateFor(game.key);
  const relay = row?.live?.phase.state ?? null;
  const state: AnchorState | null = relay
    ? { statusId: relay.statusId, awayScore: relay.awayScore, homeScore: relay.homeScore, gameClock: relay.gameClock }
    : polled ? { statusId: polled.statusId, awayScore: polled.awayScore, homeScore: polled.homeScore, gameClock: polled.gameClock, periodLabel: polled.periodLabel } : null;
  const parts = anchorParts({
    away: game.awayCode, home: game.homeCode, nowMs, sport: game.sport, state,
    configVersion: row?.live?.baseline.configVersion ?? null,
    mode: row ? (row.live?.shadow === false ? 'live' : 'practice') : null,
  });
  return (
    <div
      className={cn('text-muted-foreground truncate whitespace-nowrap font-mono', compact ? 'text-[9px]' : 'text-[10px]', className)}
      title="Time (Pacific), game clock and score — so a screenshot of this screen carries its own anchor"
      data-testid="penelope-anchor"
    >
      {parts.join(' · ')}
    </div>
  );
}
